import { useMemo, useState } from "react";
import { FiCheck, FiPlus } from "react-icons/fi";
import { Trans, useTranslation } from "react-i18next";
import { AppButton, SelectInput } from "@/components/ui/primitives";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import { useBudget, useCreateBudget, useDeleteBudget, useUpdateBudget } from "@/hooks/useBudget";
import { useCategories } from "@/hooks/useCategories";
import { useStandardPeriodRange } from "@/hooks/useStandardPeriodRange";
import { formatCurrency, formatPercent as formatPercentValue } from "@/utils/formatters";
import { NewBudgetModal } from "../components/NewBudgetModal";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { useNavigationState } from "@/hooks/useNavigationState";
import { SearchInput } from "@/components/ui/SearchInput";
import { EntityTransactionsModal } from "@/domains/accounts/components/AccountTransactionsModal";
import { useSwipeActions } from "@/hooks/useSwipeActions";
import { BudgetCard } from "../components/BudgetCard";
import { getBudgetStatus, getUsage } from "../utils/budgetStatus";
import "../budget.css";

interface BudgetRow {
  id: number;
  name: string;
  amount: number;
  category_id?: number;
  spent_amount: number;
  remaining_amount: number;
  description?: string | null;
}

interface SelectedBudget {
  id: number;
  category_id: number;
  amount: number;
  description?: string | null;
}

type BudgetSort = "usage-desc" | "usage-asc" | "name-asc" | "name-desc";

const formatMoney = (value: number) => formatCurrency(value);

const formatPercent = (value: number) =>
  formatPercentValue(value, undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const isAttentionBudget = (budget: BudgetRow) => {
  const usage = getUsage(budget.spent_amount, budget.amount);
  return usage >= 80 || budget.spent_amount > budget.amount;
};

export const BudgetView = () => {
  const { t } = useTranslation(["common", "budget"]);
  const { selectedPeriod, setSelectedPeriod, customStart, setCustomStart, customEnd, setCustomEnd, selectedRange } =
    useStandardPeriodRange("this-month", "budget");
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useNavigationState("budget.searchTerm", "");
  const [sortBy, setSortBy] = useNavigationState<BudgetSort>("budget.sortBy", "usage-desc");
  const [editingBudget, setEditingBudget] = useState<SelectedBudget | null>(null);
  const [detailsBudget, setDetailsBudget] = useState<BudgetRow | null>(null);
  const {
    swipedId: swipedBudgetId,
    setSwipedId: setSwipedBudgetId,
    gestureRef,
    handlePressStart,
    handlePressMove,
    handlePressEnd,
  } = useSwipeActions<number>();

  const { data: budgetData, isLoading, error } = useBudget({
    billable_date_gte: selectedRange.start,
    billable_date_lte: selectedRange.end,
    amount: "desc",
  });
  const { data: categories = [] } = useCategories();
  const createBudgetMutation = useCreateBudget();
  const updateBudgetMutation = useUpdateBudget();
  const deleteBudgetMutation = useDeleteBudget();

  const categoryMap = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);

  const budgetsBalance = useMemo<BudgetRow[]>(
    () =>
      (budgetData?.budgets_balance || []).map((budget) => ({
        ...budget,
        name: categoryMap.get(budget.category_id ?? -1) ?? budget.name,
      })),
    [budgetData, categoryMap],
  );

  const sortedBudgets = useMemo(() => {
    return [...budgetsBalance].sort((a, b) => {
      if (sortBy === "name-asc" || sortBy === "name-desc") {
        const difference = a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });
        return sortBy === "name-desc" ? -difference : difference;
      }

      const usageDiff = getUsage(b.spent_amount, b.amount) - getUsage(a.spent_amount, a.amount);
      if (usageDiff !== 0) return sortBy === "usage-asc" ? -usageDiff : usageDiff;

      return sortBy === "usage-asc" ? a.spent_amount - b.spent_amount : b.spent_amount - a.spent_amount;
    });
  }, [budgetsBalance, sortBy]);

  const filteredBudgets = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return sortedBudgets;
    return sortedBudgets.filter((budget) =>
      [budget.name, budget.description].some((value) => value?.toLocaleLowerCase().includes(query)),
    );
  }, [searchTerm, sortedBudgets]);
  const attentionBudgets = useMemo(() => filteredBudgets.filter(isAttentionBudget), [filteredBudgets]);
  const belowThresholdBudgets = useMemo(() => filteredBudgets.filter((budget) => !isAttentionBudget(budget)), [filteredBudgets]);

  const totals = budgetData || {
    total_budgeted: 0,
    total_spent: 0,
    remaining_budget: 0,
    budgets_balance: [],
  };

  const overBudgetCount = sortedBudgets.filter((budget) => getBudgetStatus(budget.spent_amount, budget.amount) === "over").length;
  const nearLimitCount = sortedBudgets.filter((budget) => getBudgetStatus(budget.spent_amount, budget.amount) === "near").length;

  const primaryInsight =
    overBudgetCount > 0
      ? t("budget.insightOver", { count: overBudgetCount })
      : nearLimitCount > 0
        ? t("budget.insightNear", { count: nearLimitCount })
        : t("budget.insightSafe");

  const heroAmount = formatMoney(Math.abs(totals.remaining_budget));
  const heroSub = totals.remaining_budget >= 0 ? t("budget.heroRemainingSubtitle") : t("budget.heroOverspentContext");
  const heroTotalAssigned = formatMoney(Math.max(0, totals.total_budgeted));

  const openBudgetEditor = (budget: BudgetRow) => {
    if (!budget.category_id) return;
    setEditingBudget({
      id: budget.id,
      category_id: budget.category_id,
      amount: budget.amount,
      description: budget.description ?? null,
    });
    setIsBudgetModalOpen(true);
  };

  const handleAddBudget = () => {
    setEditingBudget(null);
    setIsBudgetModalOpen(true);
  };

  const handleBudgetClick = (budget: BudgetRow) => {
    if (gestureRef.current.action === "swipe") {
      gestureRef.current.action = "tap";
      return;
    }

    if (swipedBudgetId === budget.id) {
      setSwipedBudgetId(null);
      return;
    }

    if (budget.category_id != null) setDetailsBudget(budget);
  };

  const handleEditBudgetFromList = (budget: BudgetRow) => {
    openBudgetEditor(budget);
    setSwipedBudgetId(null);
  };

  const handleDeleteBudgetFromList = async (budget: BudgetRow) => {
    const confirmed = await confirmAction({
      title: t("common.prompts.deleteBudget"),
      body: budget.name,
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) {
      setSwipedBudgetId(null);
      return;
    }

    try {
      await deleteBudgetMutation.mutateAsync(budget.id);
      setSwipedBudgetId(null);
    } catch {
      showAppMessage({ title: t("common.errors.deleteBudget"), tone: "danger" });
    }
  };

  const renderBudgetItems = (budgets: BudgetRow[]) => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {budgets.map((budget) => {
        const usage = getUsage(budget.spent_amount, budget.amount);
        const clampedUsage = Math.min(100, Math.max(0, usage));
        const status = getBudgetStatus(budget.spent_amount, budget.amount);
        const overAmount = Math.max(0, budget.spent_amount - budget.amount);
        const remaining = Math.max(0, budget.remaining_amount);

        const detailAmount = formatMoney(status === "over" ? overAmount : remaining);
        const detailLabel = status === "over" ? t("budget.exceededLabel") : t("budget.remainingLabel");
        const statusLabel =
          status === "over"
            ? t("budget.statusExceeded")
            : status === "near"
              ? t("budget.statusNearLimit")
              : t("budget.statusInControl");

        return (
          <BudgetCard
            key={budget.id}
            name={budget.name}
            spent={formatMoney(Math.max(0, budget.spent_amount))}
            amountConnector={t("budget.ofLimit")}
            amount={formatMoney(Math.max(0, budget.amount))}
            percent={clampedUsage}
            percentLabel={`${formatPercent(usage)}%`}
            detailAmount={detailAmount}
            detailLabel={detailLabel}
            status={status}
            statusLabel={statusLabel}
            editLabel={t("common:actions.edit")}
            deleteLabel={t("common:actions.delete")}
            progressLabel={t("budget.itemUsage", { percent: formatPercent(usage) })}
            progressValueText={t("budget.itemUsage", { percent: formatPercent(usage) })}
            isSwiped={swipedBudgetId === budget.id}
            menuLabel={t("budget.budgetActionsTitle", { name: budget.name })}
            canEdit={Boolean(budget.category_id)}
            onClick={() => handleBudgetClick(budget)}
            onEdit={() => handleEditBudgetFromList(budget)}
            onDelete={() => void handleDeleteBudgetFromList(budget)}
            onPointerDown={(event) => handlePressStart(event, budget.id)}
            onPointerMove={(event) => handlePressMove(event, budget.id)}
            onPointerUp={handlePressEnd}
            onPointerLeave={handlePressEnd}
            onPointerCancel={handlePressEnd}
          />
        );
      })}
    </div>
  );

  if (isLoading) {
    return <PageSkeleton lines={5} />;
  }

  if (error) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="font-medium text-[var(--app-text)]">{t("budget.loadError", { message: error.message })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page budget-page">
      <div className="app-container max-w-none !overflow-visible">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 md:max-w-none">
          <DomainTwoColumnLayout className="!h-auto !overflow-visible">
            <DomainSidebar className="lg:sticky lg:top-32 lg:h-fit lg:overflow-visible">
            <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.budget")} />

            <section className="app-domain-sidebar-section">
              <p className="budget-hero-label">{heroSub}</p>
              <p className="app-domain-sidebar-value">{heroAmount}</p>
              <p className="budget-hero-assigned">
                <Trans t={t} i18nKey="budget.heroTotalAssigned" values={{ amount: heroTotalAssigned }} components={{ strong: <strong /> }} />
              </p>
            </section>
            </DomainSidebar>

            <DomainTwoColumnContent className="!h-auto !overflow-visible pb-20">
            {sortedBudgets.length === 0 ? (
              <section className="pb-6">
                <div className="app-panel-raised px-5 py-6">
                  <p className="text-base text-[var(--app-text-secondary)]">{t("budget.emptyStateBody")}</p>
                  <AppButton
                    type="button"
                    tone="primary"
                    shape="pill"
                    className="mt-5"
                    onClick={handleAddBudget}
                    leadingIcon={<FiPlus className="h-4 w-4" />}
                  >
                    {t("budget.addFirst")}
                  </AppButton>
                </div>
              </section>
            ) : (
              <section className="pb-8">
                <div className="sticky top-2 z-10 -mx-2 mb-5 px-2 lg:top-24">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                    <SearchInput
                      className="min-w-0 flex-1"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder={t("budget.searchPlaceholder")}
                      label={t("budget.searchPlaceholder")}
                    />
                    <label className="sr-only" htmlFor="budget-cycle">{t("common.labels.cycle")}</label>
                    <SelectInput
                      id="budget-cycle"
                      value={selectedPeriod ?? "this-month"}
                      onChange={(event) => setSelectedPeriod(event.target.value as "this-month" | "last-month" | "custom")}
                      aria-label={t("common.labels.cycle")}
                      className="xl:w-auto h-[52px] rounded-[var(--ds-radius-md)] border border-[var(--ds-ink)] px-4 py-3 text-sm font-medium text-[var(--ds-ink)]"
                    >
                      <option value="this-month">{t("budget.cycleCurrent")}</option>
                      <option value="last-month">{t("budget.cycleLast")}</option>
                      <option value="custom">{t("budget.cycleCustom")}</option>
                    </SelectInput>
                    <label className="sr-only" htmlFor="budget-sort">{t("accounts.sortByLabel")}</label>
                    <SelectInput id="budget-sort" value={sortBy} onChange={(event) => setSortBy(event.target.value as BudgetSort)} aria-label={t("accounts.sortByLabel")} className="xl:w-auto h-[52px] rounded-[var(--ds-radius-md)] border border-[var(--ds-ink)] px-4 py-3 text-sm font-medium text-[var(--ds-ink)]">
                      <option value="usage-desc">{t("budget.sortUsage")} ↓</option>
                      <option value="usage-asc">{t("budget.sortUsage")} ↑</option>
                      <option value="name-asc">{t("accounts.sortByName")} A–Z</option>
                      <option value="name-desc">{t("accounts.sortByName")} Z–A</option>
                    </SelectInput>
                  </div>
                  {selectedPeriod === "custom" ? (
                    <div className="app-custom-cycle-panel mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="app-domain-sidebar-control-label">
                        <span>{t("common.labels.startDate")}</span>
                        <input type="date" value={customStart} max={customEnd} onChange={(event) => setCustomStart(event.target.value)} className="app-domain-sidebar-date-input" />
                      </label>
                      <label className="app-domain-sidebar-control-label">
                        <span>{t("common.labels.endDate")}</span>
                        <input type="date" value={customEnd} min={customStart} onChange={(event) => setCustomEnd(event.target.value)} className="app-domain-sidebar-date-input" />
                      </label>
                    </div>
                  ) : null}
                </div>

                <aside className="mb-6 flex items-start gap-3 rounded-[var(--ds-radius-md)] bg-[var(--ds-card-lilac)] p-4 text-sm leading-5 text-[var(--ds-on-card)]" aria-label={t("budget.jerryTipLabel")}>
                  <img src="/brand/jerry-face-mark-ink.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0 object-contain" />
                  <div className="flex-1">
                    <p>{primaryInsight}</p>
                    <button
                      type="button"
                      onClick={handleAddBudget}
                      className="mt-3 inline-flex items-center justify-center rounded-full border border-[var(--ds-on-card)] px-4 py-2 text-xs font-medium text-[var(--ds-on-card)] hover:opacity-80"
                    >
                      {t("budget.jerryTipAction")}
                    </button>
                  </div>
                </aside>

                {filteredBudgets.length === 0 ? (
                  <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">
                    {t("common.messages.noResults", { defaultValue: "No se encontraron resultados." })}
                  </p>
                ) : (
                  <>
                    <div>
                      <div className="mb-3">
                        <h2 className="text-xl font-medium leading-6 tracking-[-0.01em] text-[var(--ds-ink)]">{t("budget.listTitle")}</h2>
                        <p className="mt-1 text-sm leading-5 text-[var(--ds-ink-muted)]">
                          {t("budget.countLabel", { count: attentionBudgets.length })}
                        </p>
                      </div>
                      {attentionBudgets.length > 0 ? (
                        renderBudgetItems(attentionBudgets)
                      ) : (
                        <div role="status" className="flex items-start gap-3 rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-raised)] px-4 py-3 text-sm leading-5 text-[var(--ds-ink)]">
                          <FiCheck className="mt-1 h-5 w-5 shrink-0" aria-hidden="true" />
                          <div>
                            <p className="text-xs font-medium uppercase tracking-widest text-[var(--ds-ink-muted)]">{t("budget.listTitle")}</p>
                            <p className="mt-0.5">{t("budget.allClear", { count: attentionBudgets.length })}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-8">
                      <div className="mb-3">
                        <h2 className="text-xl font-medium leading-6 tracking-[-0.01em] text-[var(--ds-ink)]">{t("budget.belowThresholdTitle")}</h2>
                        <p className="mt-1 text-sm leading-5 text-[var(--ds-ink-muted)]">
                          {t("budget.countLabel", { count: belowThresholdBudgets.length })}
                        </p>
                      </div>
                      {belowThresholdBudgets.length > 0 ? (
                        renderBudgetItems(belowThresholdBudgets)
                      ) : null}
                    </div>
                  </>
                )}
              </section>
            )}
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>

        <NewBudgetModal
          isOpen={isBudgetModalOpen}
          onClose={() => {
            setIsBudgetModalOpen(false);
            setEditingBudget(null);
          }}
          categories={categories}
          assignedCategoryIds={budgetsBalance
            .map((item) => item.category_id)
            .filter((id): id is number => typeof id === "number")}
          initialData={editingBudget}
          onSubmit={async (data) => {
            if (editingBudget) {
              await updateBudgetMutation.mutateAsync({ id: editingBudget.id, data });
            } else {
              await createBudgetMutation.mutateAsync(data);
            }
          }}
        />
        {detailsBudget?.category_id != null ? (
          <EntityTransactionsModal
            entityType="budget"
            entityId={detailsBudget.category_id}
            entityName={detailsBudget.name}
            periodBalance={Number(detailsBudget.remaining_amount)}
            start={selectedRange.start}
            end={selectedRange.end}
            onClose={() => setDetailsBudget(null)}
          />
        ) : null}


      </div>
    </div>
  );
};
